import { type FC, useContext } from 'react';
import classNames from 'classnames';
import { graphql, useFragment } from 'react-relay';

import { Link } from 'dibs-elements/exports/Link.tsx';
import { FormattedMessage } from 'dibs-react-intl';

import { GetHelpZendeskContext } from './GetHelpZendeskContext.ts';
import { STEPS, ACTIONS } from './GetHelpZendeskConstants.ts';
import { type Step } from './GetHelpZendeskTypes.ts';
import { trackGetHelpZendeskEvent } from './getHelpZendeskTracking.ts';

import { type GetHelpZendeskFooter_viewer$key } from './__generated__/GetHelpZendeskFooter_viewer.graphql.ts';

import dibsCss from 'dibs-css';

const TRIGGER_TRACKING_MAP: Partial<Record<Step, string>> = {
    [STEPS.ORDER_INFO]: 'order info',
    [STEPS.NO_SEARCH_RESULTS]: 'no search results',
};

const GetHelpZendeskFooter: FC<{ viewer: GetHelpZendeskFooter_viewer$key }> = ({
    viewer: viewerRef,
}) => {
    const {
        state: { step, isCheckout, isSeller, showFurtherSupport, displayFooter },
        dispatch,
    } = useContext(GetHelpZendeskContext);

    const viewer = useFragment(
        graphql`
            fragment GetHelpZendeskFooter_viewer on Viewer {
                zendeskArticles(isSeller: $isSeller, sellerId: $sellerId) {
                    article(articleId: $articleId) @include(if: $hasArticleId) {
                        title
                    }
                }
            }
        `,
        viewerRef
    );

    const showFooter =
        (step &&
            (
                [
                    STEPS.ORDER_INFO,
                    STEPS.ARTICLE,
                    STEPS.NO_SEARCH_RESULTS,
                    STEPS.CMS_TOPIC,
                ] as Step[]
            ).includes(step)) ||
        (step === STEPS.INTRO && isCheckout) ||
        (displayFooter && step !== STEPS.CONTACT_SUPPORT && step !== STEPS.ERROR);

    if (!showFooter) {
        return null;
    }

    let trackingTrigger = '';

    if (step === STEPS.ARTICLE) {
        trackingTrigger = viewer?.zendeskArticles?.article?.title || '';
    } else {
        trackingTrigger = TRIGGER_TRACKING_MAP?.[step] || '';
    }

    return (
        <div className={classNames(dibsCss.pySmall, dibsCss.pxMedium, dibsCss.bgMoonstone)}>
            <Link
                onClick={() => {
                    if (showFurtherSupport) {
                        dispatch({
                            type: ACTIONS.SET_STEP,
                            step: STEPS.CONTACT_SUPPORT,
                        });
                        trackGetHelpZendeskEvent({
                            stepInteractionName: 'further support click',
                            isSeller,
                        });
                    } else {
                        dispatch({
                            type: ACTIONS.SET_STEP,
                            step: STEPS.CONTACT_SUPPORT_SEARCH,
                        });
                        trackGetHelpZendeskEvent({
                            stepInteractionName: 'contact support channels',
                            trigger: trackingTrigger,
                            isSeller,
                        });
                    }
                }}
                underline="underline"
            >
                <span className={dibsCss.sassyFontBodyMedium}>
                    {showFurtherSupport ? (
                        <FormattedMessage
                            id="getHelp.GetHelpZendeskFooter.furtherSupport"
                            defaultMessage="Need further support?"
                        />
                    ) : (
                        <FormattedMessage
                            id="getHelp.GetHelpZendeskFooter.title"
                            defaultMessage="Contact Support"
                        />
                    )}
                </span>
            </Link>
        </div>
    );
};

export { GetHelpZendeskFooter };
